import moment from 'moment';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useObjectVal } from 'react-firebase-hooks/database';
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table';
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { AbilityContext, Can } from '../../auth/Can';
import {
  ArchiveIcon,
  PauseIcon,
  PlayIcon,
} from '../../components/Icons';
import { Loader } from '../../components/Loader';
import { DefaultColumnFilter } from '../../components/table/DefaultFilter';
import { resetPageIndexOnFilterAction } from '../../components/table/resetPageIndexOnFilterAction';
import { logger } from '../../logging';
import { getDAMApp } from '../../services/firebase';
import {
  State,
  WorkflowState,
  WorkflowSubtypeMap,
  workflowTypesMap,
} from './types';
import { WorkflowModal } from './WorkflowModal';
import { renderWorkflowDescription } from './workflowDescription';
import { WorkflowsTable } from './WorkflowsTable';
import {
  canRunWorkflowNowInModal,
  compareWorkflowsByDisplayStatus,
  descriptionColumnFilterFn,
  getDescriptionSearchValue,
  getReadableWorkflowLifecycleStage,
  getSchedulerStatusBadgeTone,
  getWorkflowDisplayStatus,
  getWorkflowErrorShortLabel,
  getWorkflowGlobalSearchValue,
  getWorkflowStatusLabel,
  isWorkflowRetryableError,
  WorkflowDisplayStatus,
  WorkflowSearchScope,
  WorkflowTrafficLightTone,
} from './utils';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref } from '../../utils/firebase';
import { equalTo, orderByChild, query, update } from 'firebase/database';
const R = require('ramda');

const pagesStyles = require('../pages.css');
const workflowsStyles = require('./workflows.css');
const iconStyles = require('../../components/icons.css');

export type WorkflowsToolbarApi = {
  exportCsvData: any[];
  exportFilename: string;
  columns: any[];
  userCols: any;
  tableName: string;
  hiddenByDefaultIds: string[];
  alwaysHiddenIds: string[];
};

const now = moment().unix();

// UAT default: Type and legacy End date hidden (and not toggleable in the picker).
const HIDDEN_BY_DEFAULT = ['type', 'endDate'];

// Not toggleable — excluded from the table and the column picker.
// Type is always Migrate Contacts; End date is legacy (30-day no-contact archival replaced it).
const ALWAYS_HIDDEN_COLUMNS = ['type', 'endDate'];

// Sort key for workflows that have never run (sorts below any real unix timestamp)
const NO_EXECUTION_SORT_VALUE = 0;

/** Same fallback chain as the Last Execution column accessor. */
const getWorkflowExecutionSortValue = (row: {
  last_execution?: string | number;
  created?: string | number;
}) => {
  if (row.last_execution) {
    const parsed = parseInt(`${row.last_execution}`, 10);
    if (!isNaN(parsed)) {
      return parsed;
    }
  }
  if (row.created) {
    const parsed = parseInt(`${row.created}`, 10);
    if (!isNaN(parsed)) {
      return parsed;
    }
  }
  return NO_EXECUTION_SORT_VALUE;
};

const compareByLastExecutionDesc = (a: any, b: any) =>
  getWorkflowExecutionSortValue(b) - getWorkflowExecutionSortValue(a);

const compareByCreatedDesc = (a: any, b: any) => {
  const aVal = parseInt(`${a.created || 0}`, 10) || 0;
  const bVal = parseInt(`${b.created || 0}`, 10) || 0;
  return bVal - aVal;
};

// Green = action available; red = action blocked (visual cue for operators)
const getActionButtonClass = (disabled: boolean) =>
  `${workflowsStyles.actionButton} ${
    disabled
      ? workflowsStyles.actionButtonDisabled
      : workflowsStyles.actionButtonEnabled
  }`;

/** Archived/deleted workflows have no scheduler — exclude from Errored tab and block state actions. */
const isArchivedOrDeletedWorkflow = (workflow: {
  isArchived?: boolean;
  status?: string;
}) =>
  workflow.isArchived === true || workflow.status === State.DELETED;

/**
 * Tabs are mutually exclusive and keyed off the derived display status:
 * - Paused: manually paused (scheduler paused, no fatal last-run error)
 * - Errored: fatal last-run failure that needs intervention
 * - Active: still scheduled — healthy last run or retrying (rate limit / timeout)
 */
const filterWorkflowsForTab = (
  workflowsList: Record<string, any>,
  tab: { archived?: boolean; paused?: boolean; errored?: boolean }
) =>
  Object.keys(workflowsList).filter((k) => {
    const displayStatus = getWorkflowDisplayStatus(workflowsList[k]);

    if (tab.archived === true) {
      return displayStatus === WorkflowDisplayStatus.ARCHIVED;
    }
    if (tab.paused === true) {
      return displayStatus === WorkflowDisplayStatus.PAUSED;
    }
    if (tab.errored === true) {
      return displayStatus === WorkflowDisplayStatus.ERRORED;
    }

    return displayStatus === WorkflowDisplayStatus.ACTIVE;
  });

const formatUnixDate = (value?: string | number) => {
  const parsed = parseInt(`${value}`, 10);
  if (!value || isNaN(parsed)) {
    return '';
  }
  return moment.unix(parsed).format('YYYY-MM-DD HH:mm:ss');
};

/** Traffic-light badge for the derived (discriminated) workflow status. */
const StatusBadge = ({
  workflow,
  detailed = false,
}: {
  workflow: any;
  detailed?: boolean;
}) => {
  const displayStatus = getWorkflowDisplayStatus(workflow);

  if (detailed) {
    let toneClass: string;
    switch (displayStatus) {
      case WorkflowDisplayStatus.ERRORED:
        toneClass = workflowsStyles.statusBadgeRed;
        break;
      case WorkflowDisplayStatus.PAUSED:
        toneClass = workflowsStyles.statusBadgeAmber;
        break;
      case WorkflowDisplayStatus.ARCHIVED:
        toneClass = workflowsStyles.statusBadgeNeutral;
        break;
      default:
        toneClass = workflowsStyles.statusBadgeGreen;
    }

    return (
      <span className={`${workflowsStyles.statusBadge} ${toneClass}`}>
        {getWorkflowStatusLabel(workflow)}
      </span>
    );
  }

  let label: string;
  let toneClass: string;
  switch (displayStatus) {
    case WorkflowDisplayStatus.ERRORED:
      label = 'Error';
      toneClass = workflowsStyles.statusBadgeRed;
      break;
    case WorkflowDisplayStatus.PAUSED:
      label = 'Paused';
      toneClass = workflowsStyles.statusBadgeAmber;
      break;
    case WorkflowDisplayStatus.ARCHIVED:
      label = 'Archived';
      toneClass = workflowsStyles.statusBadgeNeutral;
      break;
    default:
      if (
        workflow.last_execution_result === 'running' ||
        workflow.last_execution_result === 'retrying'
      ) {
        label = 'Running';
        toneClass = workflowsStyles.statusBadgeBlue;
      } else if (workflow.last_execution_result === 'success') {
        label = 'Success';
        toneClass = workflowsStyles.statusBadgeGreen;
      } else {
        label = 'Active';
        toneClass = workflowsStyles.statusBadgeGreen;
      }
  }

  return (
    <span className={`${workflowsStyles.statusBadge} ${toneClass}`}>
      {label}
    </span>
  );
};

const SCHEDULER_STATUS_TONE_CLASS: Record<WorkflowTrafficLightTone, string> = {
  green: workflowsStyles.statusBadgeGreen,
  amber: workflowsStyles.statusBadgeAmber,
  red: workflowsStyles.statusBadgeRed,
  neutral: workflowsStyles.statusBadgeNeutral,
};

/** Raw Cloud Scheduler state (enabled / paused / deleted). */
const SchedulerStatusCell = ({ workflow }: { workflow: any }) => {
  const status = workflow.status;
  let badgeLabel = 'Enabled';

  if (status === State.PAUSED) {
    badgeLabel = 'Paused';
  } else if (status === State.DELETED) {
    badgeLabel = 'Deleted';
  } else if (status !== State.ENABLED) {
    badgeLabel = status || 'Unknown';
  }

  const toneClass =
    SCHEDULER_STATUS_TONE_CLASS[getSchedulerStatusBadgeTone(workflow)];

  return (
    <span className={`${workflowsStyles.statusBadge} ${toneClass}`}>
      {badgeLabel}
    </span>
  );
};

const workflowGlobalFilterFn = (rows, _columnIds, filterValue) => {
  const query = String(filterValue || '').trim().toLowerCase();
  if (!query) {
    return rows;
  }

  return rows.filter((row) =>
    getWorkflowGlobalSearchValue(row.original).toLowerCase().includes(query)
  );
};

workflowGlobalFilterFn.autoRemove = (val) => !val;

const WorkflowRowActions = ({
  row,
  user,
  isWorkflowsAdmin,
  isPrivacyWorkflowsAdmin,
  onStateChange,
  onArchive,
}: {
  row: { index: number; original: any };
  user: { email?: string };
  isWorkflowsAdmin: boolean;
  isPrivacyWorkflowsAdmin: boolean;
  onStateChange: (state: WorkflowState, workflowid: string) => Promise<void>;
  onArchive: (rowIndex: number, key: string) => Promise<void>;
}) => {
  const [loading, setLoading] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const status = row.original.status;
  const isArchivedOrDeleted = isArchivedOrDeletedWorkflow(row.original);
  const isPrivacy = row.original.args?.is_privacy === true;
  const createdByEmail = row.original.createdBy?.email;
  const canResume =
    isPrivacy === false ||
    (isPrivacy === true &&
      user.email !== createdByEmail &&
      isPrivacyWorkflowsAdmin);

  const resumeDisabled =
    status === State.ENABLED ||
    !canResume ||
    isArchivedOrDeleted;
  const pauseDisabled = status !== State.ENABLED;
  const runNowDisabled =
    isArchivedOrDeleted ||
    (status === State.ENABLED
      ? false
      : status === State.PAUSED
        ? !canRunWorkflowNowInModal(row.original)
        : true);
  const canArchive = isWorkflowsAdmin && status !== State.ENABLED;

  const runAction = (
    e: React.MouseEvent,
    action: () => Promise<void>
  ) => {
    e.stopPropagation();
    setLoading(true);
    action().finally(() => setLoading(false));
  };

  return (
    <>
      <div className={workflowsStyles.actionsRow}>
        <Button
          className={getActionButtonClass(resumeDisabled)}
          title={
            isArchivedOrDeleted
              ? 'Cannot resume — workflow is archived'
              : 'Resume'
          }
          disabled={resumeDisabled || loading}
          onClick={(e) =>
            runAction(e, () =>
              onStateChange(WorkflowState.RESUME, row.original.key)
            )
          }
        >
          <PlayIcon fill="white" class={iconStyles.tick} />
        </Button>

        <Button
          className={getActionButtonClass(pauseDisabled)}
          title="Pause"
          disabled={pauseDisabled || loading}
          onClick={(e) =>
            runAction(e, () =>
              onStateChange(WorkflowState.PAUSE, row.original.key)
            )
          }
        >
          <PauseIcon fill="white" class={iconStyles.tick} />
        </Button>

        <Button
          className={getActionButtonClass(runNowDisabled)}
          title={
            isArchivedOrDeleted
              ? 'Cannot run — workflow is archived'
              : 'Run Now'
          }
          disabled={runNowDisabled || loading}
          onClick={(e) =>
            runAction(e, () =>
              onStateChange(WorkflowState.RUN_NOW, row.original.key)
            )
          }
        >
          Run Now
        </Button>

        <Can I="manage" a="workflows_admin">
          <Button
            className={getActionButtonClass(!canArchive)}
            title="Archive"
            disabled={!canArchive || loading}
            onClick={(e) => {
              e.stopPropagation();
              if (canArchive) {
                setArchiveConfirmOpen(true);
              }
            }}
          >
            <ArchiveIcon fill="white" class={iconStyles.archive} />
          </Button>
        </Can>

        <Loader loading={loading && !archiveConfirmOpen} />
      </div>
      <Modal
        isOpen={archiveConfirmOpen}
        toggle={() => {
          if (!loading) {
            setArchiveConfirmOpen(false);
          }
        }}
        backdrop={loading ? 'static' : true}
        keyboard={!loading}
      >
        <ModalHeader
          toggle={
            loading ? undefined : () => setArchiveConfirmOpen(false)
          }
        >
          Archive workflow?
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <div className={workflowsStyles.archiveProcessing}>
              <img
                src="/images/loading.gif"
                alt=""
                className={workflowsStyles.archiveProcessingSpinner}
              />
              <div>
                <div className={workflowsStyles.archiveProcessingTitle}>
                  Archiving workflow…
                </div>
                <div className={workflowsStyles.archiveProcessingCaption}>
                  Removing the scheduler and moving this workflow to Archived.
                  This can take a few seconds.
                </div>
              </div>
            </div>
          ) : (
            `Archive "${row.original.name || row.original.key}"? The workflow will be moved to the Archived tab and its scheduler will be removed.`
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            disabled={loading}
            onClick={(e) => {
              e.stopPropagation();
              runAction(e, async () => {
                await onArchive(row.index, row.original.key);
                setArchiveConfirmOpen(false);
              });
            }}
          >
            {loading ? 'Archiving…' : 'Archive'}
          </Button>{' '}
          <Button
            color="secondary"
            onClick={() => setArchiveConfirmOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export const Workflows = ({
  archived,
  paused,
  errored,
  isActive = false,
  onToolbarChange,
  globalSearchQuery = '',
  searchScope = 'tab',
}: {
  archived?: boolean;
  paused?: boolean;
  errored?: boolean;
  isActive?: boolean;
  onToolbarChange?: (api: WorkflowsToolbarApi | null) => void;
  globalSearchQuery?: string;
  searchScope?: WorkflowSearchScope;
}) => {
  const damApp = getDAMApp();
  const functions = getFunctions(damApp);
  const damAuth = getAuth(damApp);
  const TABLE_NAME = 'workflows';
  const [user, userLoading] = useAuthState(damAuth as any);

  const workflowsRef = ref('workflows');
  const workflowsArchivedQuery = query(
    workflowsRef,
    orderByChild('isArchived'),
    equalTo(true)
  );

  const isAllStatusesSearch =
    searchScope === 'all' && Boolean(globalSearchQuery.trim());

  let workflowsExportFilenamePrefix = 'active';

  const workflowsActiveQuery = workflowsRef;
  // because apparently workflows don't default isArchived to false
  let workflowsQuery;
  if (isAllStatusesSearch) {
    // Need every status in memory so All statuses search can match across tabs
    workflowsQuery = workflowsActiveQuery;
    workflowsExportFilenamePrefix = 'all-statuses';
  } else if (archived === true) {
    workflowsQuery = workflowsArchivedQuery;
    workflowsExportFilenamePrefix = 'archived';
  } else if (paused === true) {
    workflowsQuery = workflowsActiveQuery;
    workflowsExportFilenamePrefix = 'paused';
  } else if (errored === true) {
    workflowsQuery = workflowsActiveQuery;
    workflowsExportFilenamePrefix = 'errored';
  } else {
    workflowsQuery = workflowsActiveQuery;
  }

  const [workflowsList, loading, error] = useObjectVal(workflowsQuery as any);
  const [values, setValues] = useState<any[]>([]);
  const [valuesLoading, setValuesLoading] = useState(false);

  const uid = damAuth.currentUser?.uid;
  const userColsRef = uid
    ? ref(`user_preferences/${uid}/columns/${TABLE_NAME}`)
    : null;
  const [userCols, userColsLoading, userColsError] = useObjectVal(
    userColsRef as any
  );

  const [workflow, setWorkflow] = useState(null);
  const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false);

  const ability = useContext(AbilityContext);
  const isWorkflowsAdmin = ability.can('manage', 'workflows_admin');
  const isPrivacyWorkflowsAdmin = ability.can('manage', 'privacy_workflows');
  const showDetailedStatus =
    paused === true || errored === true || isAllStatusesSearch;

  const buildWorkflowRows = React.useCallback(
    (sourceList: Record<string, any> | null | undefined): any[] => {
      if (!sourceList) {
        return [];
      }

      const workflowKeys = isAllStatusesSearch
        ? Object.keys(sourceList)
        : filterWorkflowsForTab(sourceList, {
            archived,
            paused,
            errored,
          });

      const data = workflowKeys.map((k) => {
        const value = sourceList[k];
        const labels = { ...value };
        return { ...labels, key: k };
      });

      if (isAllStatusesSearch) {
        return [...data].sort(compareWorkflowsByDisplayStatus);
      }

      // Stable default order in the data itself — if react-table sortBy is
      // cleared (e.g. after clearing a column filter), Active still shows
      // newest last-execution first instead of raw RTDB key order (old first).
      if (archived !== true && paused !== true && errored !== true) {
        return [...data].sort(compareByLastExecutionDesc);
      }

      return [...data].sort(compareByCreatedDesc);
    },
    [archived, paused, errored, isAllStatusesSearch]
  );

  // when workflowsList changes
  useEffect(() => {
    if (isWorkflowModalOpen === true) {
      return;
    }

    let isMounted = true;

    if (workflowsList === null) {
      if (isMounted) {
        setValuesLoading(true);
        setValues([]);
      }
    }

    if (workflowsList) {
      if (isMounted) {
        // Don't toggle loading on RTDB refreshes — unmounting the table drops
        // sort/filter state and is what made clearing Description look broken.
        setValues(buildWorkflowRows(workflowsList));
        setValuesLoading(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [workflowsList, isWorkflowModalOpen, archived, paused, errored, buildWorkflowRows]);

  const updateData = async (
    rowIndex,
    columnId,
    value,
    updateTableData,
    key
  ) => {
    const workflowsRef = ref(`workflows/${key}`);
    const workflow = {};
    workflow[columnId] = value;
    workflow['modified'] = now;
    try {
      await update(workflowsRef, workflow);
    } catch (e) {
      logger.error('[updateMyData] - error updating workflow row: ', e);
    }

    if (updateTableData) {
      logger.debug(`setting values 3`);
      setValues((old) =>
        old.map((row, index) => {
          if (index === rowIndex) {
            return {
              ...old[rowIndex],
              ...workflow,
            };
          }
          return row;
        })
      );
    }
  };

  const handleStateChange = async (
    state: WorkflowState,
    workflowid: string
  ) => {
    try {
      logger.debug(
        `[handleStateChange] [${workflowid}] changing to state: ${state}`
      );
      const callable = httpsCallable(
        functions,
        'accountManager-callableUpdateWorkflowState'
      );
      await callable({ id: workflowid, state });
    } catch (e) {
      logger.error(
        `[handleStateChange] - error changing state for workflow id: ${workflowid} to state: ${state} - ${e}`
      );
    }
  };

  // #1433 - this needs to be a new callable as it will need to delete the cloud scheduler
  const archiveWorkflow = async (rowIndex: number, key: string) => {
    logger.info(`[archiveWorkflow] rowIndex: ${rowIndex} `);

    const callable = httpsCallable(
      functions,
      'accountManager-callableArchiveWorkflow'
    );

    await callable({ id: key });

    await updateData(rowIndex, 'isArchived', true, true, key);
  };

  const handleRowClick = (row, cell, e) => {
    logger.debug(
      `[handleRowClick] row: ${JSON.stringify(row.original)} cell: ${
        cell.column.id
      }`
    );
    if (cell.column.id === 'actions') {
      return;
    }

    const clickTarget = e?.target;
    if (clickTarget instanceof Element && clickTarget.closest('a')) {
      return;
    }

    setWorkflow(row.original);
    setIsWorkflowModalOpen(true);
  };

  const RenderBooleanCell = ({
    cell: { value: initialValue },
    row: { index, original },
    column: { id },
    updateData,
  }) => {
    const [value, setValue] = React.useState(initialValue);
    return value === 'true' ? 'True' : 'False';
  };

  const isActiveTab = archived !== true && paused !== true && errored !== true;

  const columns = React.useMemo(
    () =>
      [
        {
          Header: 'ID',
          accessor: 'key',
          id: 'key',
        },
        {
          Header: 'Name',
          accessor: 'name',
          id: 'name',
          Cell: ({ value }) => (
            <div className={workflowsStyles.nameText}>{value}</div>
          ),
        },
        archived !== true
          ? {
              Header: 'Actions',
              id: 'actions',
              disableSortBy: true,
              disableFilters: true,
              className: workflowsStyles.actionsCell,
              Cell: ({ row }) => (
                <WorkflowRowActions
                  row={row}
                  user={{ email: user?.email ?? undefined }}
                  isWorkflowsAdmin={isWorkflowsAdmin}
                  isPrivacyWorkflowsAdmin={isPrivacyWorkflowsAdmin}
                  onStateChange={handleStateChange}
                  onArchive={archiveWorkflow}
                />
              ),
            }
          : null,
        {
          Header: 'Status',
          accessor: (row) => {
            const displayStatus = getWorkflowDisplayStatus(row);
            if (displayStatus === WorkflowDisplayStatus.ACTIVE) {
              if (
                row.last_execution_result === 'running' ||
                row.last_execution_result === 'retrying'
              ) {
                return 'Running';
              }
              if (row.last_execution_result === 'success') {
                return 'Success';
              }
              // Retryable last-run failures stay on Active (self-healing)
              if (row.last_execution_result === 'error') {
                return 'Retrying';
              }
              return 'Active';
            }
            return {
              [WorkflowDisplayStatus.ERRORED]: 'Error',
              [WorkflowDisplayStatus.PAUSED]: 'Paused',
              [WorkflowDisplayStatus.ARCHIVED]: 'Archived',
            }[displayStatus];
          },
          Cell: ({ row }) => (
            <StatusBadge
              workflow={row.original}
              detailed={showDetailedStatus}
            />
          ),
          id: 'status',
        },
        {
          Header: 'Daily Schedule',
          accessor: getReadableWorkflowLifecycleStage,
          id: 'lifecycle_stage',
          Cell: ({ value }) => (
            <div className={workflowsStyles.cellSub}>{value}</div>
          ),
        },
        {
          Header: 'Scheduler Status',
          accessor: (row) => row.status || '',
          id: 'scheduler_status',
          Cell: ({ row }) => <SchedulerStatusCell workflow={row.original} />,
        },
        {
          Header: 'Last Execution',
          id: 'last_execution',
          accessor: (row) => {
            if (row.last_execution) {
              return parseInt(row.last_execution, 10);
            }
            if (row.created) {
              return parseInt(row.created, 10);
            }

            return NO_EXECUTION_SORT_VALUE;
          },
          Cell: ({
            row,
          }: {
            row: { original: { last_execution?: string; status?: string } };
          }) => {
            if (row.original.last_execution) {
              return formatUnixDate(row.original.last_execution);
            }

            const onActiveTab =
              archived !== true && paused !== true && errored !== true;
            if (onActiveTab && row.original.status !== State.PAUSED) {
              return 'Scheduled';
            }

            return '—';
          },
        },
        {
          Header: 'Last Execution Result',
          accessor: (row) => {
            if (row.last_execution_result === 'retrying') {
              return 'Running';
            }
            if (row.last_execution_result) {
              return (
                row.last_execution_result.charAt(0).toUpperCase() +
                row.last_execution_result.slice(1)
              );
            }

            return '';
          },
          Cell: ({ value }) => value || '—',
          id: 'last_execution_result',
        },
        {
          Header: 'Error',
          accessor: (row) =>
            row.last_execution_result === 'error' ? row.error : '',
          Cell: ({ value }) =>
            value ? (
              <span
                className={`${workflowsStyles.errorPill} ${
                  isWorkflowRetryableError(value)
                    ? workflowsStyles.errorPillRetryable
                    : ''
                }`}
                title={value}
              >
                {getWorkflowErrorShortLabel(value)}
              </span>
            ) : (
              '—'
            ),
          id: 'error',
        },
        {
          Header: 'Created',
          accessor: (row) => formatUnixDate(row.created),
          id: 'created',
        },
        {
          Header: 'Created By',
          accessor: (row) => row.createdBy?.email || '',
          id: 'createdBy',
        },
        {
          Header: 'Modified',
          accessor: (row) => formatUnixDate(row.modified),
          id: 'modified',
        },
        {
          Header: 'Subtype',
          accessor: (row) => WorkflowSubtypeMap[row.subtype] || row.subtype || '',
          id: 'subtype',
        },
        {
          Header: 'Description',
          accessor: (row) => getDescriptionSearchValue(row),
          Cell: ({ row }) => (
            <div className={workflowsStyles.descriptionCellContent}>
              {renderWorkflowDescription(row.original.description, {
                formId: row.original.args?.form_id,
                sourceAccountId: row.original.args?.source_account,
                destinationAccountId: row.original.args?.destination_account,
              })}
            </div>
          ),
          id: 'description',
          minWidth: 320,
          width: 360,
          maxWidth: 400,
          filter: descriptionColumnFilterFn,
          className: workflowsStyles.descriptionColumn,
        },
        {
          Header: 'Privacy',
          accessor: (row) =>
            row.args?.is_privacy === true ? 'true' : 'false',
          Cell: RenderBooleanCell,
          id: 'is_privacy',
        },
        {
          Header: 'Store',
          accessor: (row) => (row.args?.is_store === true ? 'true' : 'false'),
          Cell: RenderBooleanCell,
          id: 'is_store',
        },
        {
          Header: 'Type',
          accessor: (row) => workflowTypesMap[row.type] || row.type || '',
          id: 'type',
        },
        {
          Header: 'End date',
          accessor: (row) => formatUnixDate(row.endDate),
          Cell: ({ value }) => value || '—',
          id: 'endDate',
        },
      ].filter(Boolean),
    [archived, paused, errored, showDetailedStatus, user, isWorkflowsAdmin, isPrivacyWorkflowsAdmin]
  );

  const filterTypes = React.useMemo(
    () => ({}),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  // memoize values - the heavy lifting is now done in the above useEffect
  const records = React.useMemo(() => values, [values]);

  const computeHiddenColumns = () => {
    const hidden = new Set<string>([
      ...HIDDEN_BY_DEFAULT.filter(
        (id) => !(userCols && userCols[id] === true)
      ),
      ...ALWAYS_HIDDEN_COLUMNS,
    ]);
    R.keys(R.pickBy((val, key) => val === false, userCols)).forEach((id) =>
      hidden.add(id)
    );
    return Array.from(hidden);
  };

  const defaultSortBy = React.useMemo(
    () =>
      isActiveTab
        ? [{ id: 'last_execution', desc: true }]
        : [{ id: 'created', desc: true }],
    [isActiveTab]
  );

  const tableData = useTable(
    {
      columns,
      data: records,
      filterTypes,
      defaultColumn,
      stateReducer: resetPageIndexOnFilterAction,
      globalFilter: workflowGlobalFilterFn,
      autoResetFilters: false,
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetGlobalFilter: false,
      initialState: {
        // Active tab: most recently executed workflows first; other tabs keep created desc
        sortBy: defaultSortBy,
        hiddenColumns: computeHiddenColumns(),
        globalFilter: globalSearchQuery.trim() || undefined,
        pageSize: 10,
        pageIndex: 0,
      },
      autoResetHiddenColumns: false,
      updateData,
      handleRowClick,
    } as any,
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  ) as any;

  const { setGlobalFilter, gotoPage, setSortBy, state: tableState } = tableData;
  const prevFiltersRef = React.useRef(tableState.filters);
  const prevAllStatusesSearchRef = React.useRef(isAllStatusesSearch);
  const pageIndexRef = React.useRef(0);
  const prevModalOpenRef = React.useRef(false);

  // Closing the workflow modal used to dump users back on page 1 after the
  // table data refresh — restore the page they were reviewing.
  React.useEffect(() => {
    const wasOpen = prevModalOpenRef.current;
    prevModalOpenRef.current = isWorkflowModalOpen;
    if (wasOpen && !isWorkflowModalOpen) {
      const page = pageIndexRef.current;
      const timeoutId = window.setTimeout(() => {
        gotoPage(page);
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }
  }, [isWorkflowModalOpen, gotoPage]);

  // Remember the list page while browsing (frozen while the modal is open)
  React.useEffect(() => {
    if (!isWorkflowModalOpen) {
      pageIndexRef.current = tableState.pageIndex;
    }
  }, [tableState.pageIndex, isWorkflowModalOpen]);

  React.useEffect(() => {
    setGlobalFilter(globalSearchQuery.trim() || undefined);
    gotoPage(0);
    // Leaving search should restore the tab's default order
    if (!globalSearchQuery.trim()) {
      setSortBy(defaultSortBy);
    }
  }, [
    globalSearchQuery,
    setGlobalFilter,
    gotoPage,
    setSortBy,
    defaultSortBy,
  ]);

  // Clearing Description (or any column filter) was leaving sortBy empty → RTDB
  // key order (oldest first). Re-apply the tab default when filters clear.
  React.useEffect(() => {
    const hadFilters = (prevFiltersRef.current || []).length > 0;
    const hasFilters = (tableState.filters || []).length > 0;
    prevFiltersRef.current = tableState.filters;
    if (hadFilters && !hasFilters) {
      setSortBy(defaultSortBy);
      gotoPage(0);
    }
  }, [tableState.filters, setSortBy, gotoPage, defaultSortBy]);

  // Leaving All-statuses search remounts column set; restore default sort.
  React.useEffect(() => {
    if (prevAllStatusesSearchRef.current && !isAllStatusesSearch) {
      setSortBy(defaultSortBy);
      gotoPage(0);
    }
    prevAllStatusesSearchRef.current = isAllStatusesSearch;
  }, [isAllStatusesSearch, setSortBy, gotoPage, defaultSortBy]);

  React.useEffect(() => {
    tableData.setHiddenColumns(computeHiddenColumns());
  }, [userCols]);

  React.useEffect(() => {
    if (!onToolbarChange || !isActive) {
      return;
    }

    if (loading || valuesLoading || userColsLoading || userLoading) {
      return;
    }

    onToolbarChange({
      exportCsvData: tableData.rows.map((row) => row.values) || [],
      exportFilename: `${workflowsExportFilenamePrefix}-workflows-export-csv-${moment().format(
        'DD-MM-YYYY hh:mm:ss'
      )}`,
      columns,
      userCols,
      tableName: TABLE_NAME,
      hiddenByDefaultIds: HIDDEN_BY_DEFAULT,
      alwaysHiddenIds: ALWAYS_HIDDEN_COLUMNS,
    });

    return () => onToolbarChange(null);
  }, [
    isActive,
    onToolbarChange,
    loading,
    valuesLoading,
    userColsLoading,
    userLoading,
    tableData.rows,
    columns,
    userCols,
    workflowsExportFilenamePrefix,
  ]);

  if (loading || valuesLoading || userColsLoading || userLoading) {
    return <Loader loading={true} />;
  }

  if (error) {
    return <div>{`Error: ${error.message}`}</div>;
  }

  if (userColsError) {
    return <div>Error loading cols</div>;
  }

  if (!user) {
    return <div>Error loading current user</div>;
  }

  return (
    <div className={pagesStyles.fullHeight}>
      {values && (
        <div className={`${pagesStyles.noPadding} ${pagesStyles.fullHeight}`}>
          <Row className={pagesStyles.fullHeight}>
            <Col sm={{ size: 12 }} className={pagesStyles.fullHeight}>
              <Can I="manage" a="workflows">
                <WorkflowsTable {...tableData} showFilters={true} />
              </Can>
            </Col>
          </Row>
        </div>
      )}
      <Can I="manage" a="workflows">
        {isWorkflowModalOpen && (
          <Row className={pagesStyles.fullHeight}>
            <Col sm={{ size: 12 }} className={pagesStyles.fullHeight}>
              <WorkflowModal
                workflow={workflow}
                setWorkflow={setWorkflow}
                isOpen={isWorkflowModalOpen}
                toggle={setIsWorkflowModalOpen}
                user={user}
                isWorkflowsAdmin={isWorkflowsAdmin}
              />
            </Col>
          </Row>
        )}
      </Can>
    </div>
  );
};
